import footprint, { FootprintComponentKind } from "@onefootprint/footprint-js";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";
import {
  IPaymentCard,
  PaymentCardType,
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { useWSMutation } from "@ws-react-query";
import { useCallback, useMemo } from "react";
import { useUserId } from "../../query/hooks/helpers";
import { useMutationCompleteOneTimePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationCompleteOneTimePaymentCardCreate";
import { useMutationCompletePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationCompletePaymentCardCreate";
import { useMutationInitiateOneTimePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationInitiateOneTimePaymentCardCreate";
import { useMutationInitiatePaymentCardCreate } from "../../query/paymentCards/mutations/useMutationInitiatePaymentCardCreate";
import { QUERY_PAYOUT_SETTINGS } from "../../query/payments/keys";
import { paymentsService } from "../../services/payments";
import { WSServiceError } from "../../utils/serviceHelper";
import { footprintAppearanceConfig } from "../constants/footprint";

type Config = {
  clientId?: string;
  oneTime?: boolean;
};

type OpenConfig = {
  onCancel?: () => void;
  onSuccess?: (paymentCard: IPaymentCard) => void;
  onError?: (error: WSServiceError) => void;
  variant?: "modal" | "inline";
  containerId?: string;
  setAsInstantPayoutMethod?: boolean;
  blockCreditCards?: boolean;
};

export const CREDIT_CARD_INSTANT_PAYOUT_ERROR =
  "Credit cards aren't supported for instant payout. Please add an eligible debit card to continue.";

export const usePaymentCardForm = (config?: Config) => {
  const [initiateCreate, initiateCreateMeta] =
    useMutationInitiatePaymentCardCreate();
  const [completeCreate, completeCreateMeta] =
    useMutationCompletePaymentCardCreate();
  const [initiateOneTimeCreate, initiateOneTimeCreateMeta] =
    useMutationInitiateOneTimePaymentCardCreate();
  const [completeOneTimeCreate, completeOneTimeCreateMeta] =
    useMutationCompleteOneTimePaymentCardCreate();

  const { openSnackbar } = useWSSnackbar();

  const userId = useUserId();
  const oneTimeUserId = useMemo(
    () => (config?.oneTime ? config.clientId : undefined),
    [config?.oneTime, config?.clientId]
  );

  const initiate = useCallback(async () => {
    if (oneTimeUserId) {
      return initiateOneTimeCreate({
        userId: oneTimeUserId
      });
    }

    return initiateCreate();
  }, [initiateCreate, initiateOneTimeCreate, oneTimeUserId]);

  const [setDefaultInstantPayoutMethod, setDefaultInstantPayoutMethodMeta] =
    useWSMutation<void, WSServiceError, { paymentCardId: string }>(
      async ({ paymentCardId }) => {
        await paymentsService.payoutSettings.update(userId, {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationId: paymentCardId,
            destinationType: PayoutDestinationType.PaymentCard
          }
        });
      },
      {
        dependencies: [QUERY_PAYOUT_SETTINGS]
      }
    );

  const complete = useCallback(
    async (paymentCardId: string, config?: OpenConfig) => {
      if (oneTimeUserId) {
        return completeOneTimeCreate(
          {
            paymentCardId,
            userId: oneTimeUserId
          },
          { onSuccess: config?.onSuccess, onError: config?.onError }
        );
      }

      return completeCreate(
        {
          paymentCardId,
          saveForFutureUse: true
        },
        {
          onSuccess: paymentCard => {
            const shouldBlockCreditCard =
              config?.blockCreditCards || config?.setAsInstantPayoutMethod;

            if (
              shouldBlockCreditCard &&
              paymentCard.cardType === PaymentCardType.Credit
            ) {
              openSnackbar({
                message: CREDIT_CARD_INSTANT_PAYOUT_ERROR,
                type: "error"
              });
              return;
            }

            if (config?.setAsInstantPayoutMethod) {
              setDefaultInstantPayoutMethod(
                {
                  paymentCardId
                },
                {
                  onSuccess: () => {
                    config?.onSuccess?.(paymentCard);
                  },
                  onError: config?.onError
                }
              );
            } else {
              config?.onSuccess?.(paymentCard);
            }
          },
          onError: config?.onError
        }
      );
    },
    [
      completeCreate,
      completeOneTimeCreate,
      oneTimeUserId,
      openSnackbar,
      setDefaultInstantPayoutMethod
    ]
  );

  const open = useCallback(async (config?: OpenConfig) => {
    const paymentCardCreateInitiate = await initiate();

    if (!paymentCardCreateInitiate) return;

    const component = footprint.init({
      appearance: footprintAppearanceConfig,
      authToken: paymentCardCreateInitiate.footprintToken,
      kind: FootprintComponentKind.Form,
      variant: config?.variant || "modal",
      containerId: config?.containerId || "root",
      onCancel: config?.onCancel,
      onClose: config?.onCancel,
      options: {
        hideFootprintLogo: true
      },
      onComplete: () => {
        complete(paymentCardCreateInitiate.paymentCardId, config);

        component.destroy();
      }
    });

    component.render();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = useMemo(
    () =>
      initiateCreateMeta.isLoading ||
      completeCreateMeta.isLoading ||
      initiateOneTimeCreateMeta.isLoading ||
      completeOneTimeCreateMeta.isLoading ||
      setDefaultInstantPayoutMethodMeta.isLoading,
    [
      initiateCreateMeta.isLoading,
      completeCreateMeta.isLoading,
      initiateOneTimeCreateMeta.isLoading,
      completeOneTimeCreateMeta.isLoading,
      setDefaultInstantPayoutMethodMeta.isLoading
    ]
  );

  return {
    open,
    isLoading
  };
};
